import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import MainLayout from '../../components/layout/MainLayout';
import ObituaryForm from '../../components/obituary/ObituaryForm';
import ProgressBar from '../../components/common/ProgressBar';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useToast } from '../../context/ToastContext';

/**
 * ObituaryCreateStep3 - 부고 작성 3단계 (상주 정보 및 미리보기)
 * 모바일 전용 다단계 부고 작성 페이지
 */
const ObituaryCreateStep3 = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // localStorage에서 기존 데이터 불러오기 (렌더 중 navigate 금지 → useEffect로 이동)
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('obituaryDraft');
    if (!saved) return {};
    try {
      return JSON.parse(saved);
    } catch (e) {
      return {};
    }
  });

  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // 이전 단계(Step1·Step2) 완료 검증: 필수 선행 정보가 없으면 리다이렉트
  useEffect(() => {
    const saved = localStorage.getItem('obituaryDraft');
    let parsed = null;
    try {
      parsed = saved ? JSON.parse(saved) : null;
    } catch (e) {
      parsed = null;
    }
    if (!parsed || !parsed.deceasedName || !parsed.relationship) {
      navigate('/obituaries/create', { replace: true });
    } else if (!parsed.funeralHome || !parsed.departureDate) {
      navigate('/obituaries/create/step2', { replace: true });
    }
  }, [navigate]);

  // 폼 데이터 변경 핸들러
  const handleChange = (newData) => {
    setFormData(newData);
    // localStorage에 임시 저장
    localStorage.setItem('obituaryDraft', JSON.stringify(newData));
    // 에러 초기화
    if (errors[Object.keys(newData).find(key => newData[key] !== formData[key])]) {
      setErrors({});
    }
  };

  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};

    if (!formData.chiefMourner?.trim()) {
      newErrors.chiefMourner = '상주명을 입력해주세요.';
    }

    if (!formData.chiefMournerPhone?.trim()) {
      newErrors.chiefMournerPhone = '상주 연락처를 입력해주세요.';
    } else {
      // 전화번호 형식 검증 (간단한 패턴)
      const phonePattern = /^01[0-9]-?\d{3,4}-?\d{4}$/;
      if (!phonePattern.test(formData.chiefMournerPhone.replace(/-/g, ''))) {
        newErrors.chiefMournerPhone = '올바른 전화번호 형식이 아닙니다.';
      }
    }

    if (!formData.chiefMournerRelationship?.trim()) {
      newErrors.chiefMournerRelationship = '상주 관계를 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 이전 단계로 이동
  const handlePrev = () => {
    navigate('/obituaries/create/step2');
  };

  // 미리보기
  const handlePreview = () => {
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  // 제출
  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowPreview(false);
      return;
    }

    try {
      // Mock API 호출 (실제로는 POST 요청)
      const newObituary = {
        ...formData,
        id: Date.now(),
        userId: 1, // Mock user ID
        isFavorite: false,
        createdAt: new Date().toISOString(),
        lat: 37.5800, // Mock coordinates
        lng: 127.0020
      };

      // TODO: 실제 API 호출
      console.log('Creating obituary:', newObituary);

      // localStorage 초기화
      localStorage.removeItem('obituaryDraft');

      showToast('부고가 성공적으로 등록되었습니다.', 'success');

      // 부고 목록 또는 상세 페이지로 이동
      setTimeout(() => {
        navigate('/obituaries');
      }, 1000);
    } catch (error) {
      console.error('Failed to create obituary:', error);
      showToast('부고 등록에 실패했습니다.', 'error');
    }
  };

  // 미리보기 렌더링
  const renderPreview = () => {
    if (!formData) return null;

    const formattedDepartureDate = formData.departureDate
      ? format(new Date(formData.departureDate), 'yyyy년 MM월 dd일 (E)', { locale: ko })
      : '';

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary-900 mb-2">부고</h2>
          <div className="w-16 h-1 bg-primary-500 mx-auto mb-6" />
        </div>

        <Card>
          <div className="space-y-4">
            {/* 고인 정보 */}
            <div className="text-center border-b pb-4">
              <h3 className="text-xl font-bold text-primary-900 mb-2">
                故 {formData.deceasedName}
              </h3>
              <p className="text-sm text-gray-600">
                {formData.chiefMourner} {formData.chiefMournerRelationship}의 {formData.relationship}
              </p>
            </div>

            {/* 장례식장 정보 */}
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">빈소</span>
                <span className="text-gray-900">{formData.funeralHome} {formData.funeralRoom}</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">주소</span>
                <span className="text-gray-900">{formData.funeralAddress}</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">발인</span>
                <span className="text-gray-900">{formattedDepartureDate} {formData.departureTime}</span>
              </div>
            </div>

            {/* 상주 정보 */}
            <div className="border-t pt-4 space-y-2 text-sm">
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">상주</span>
                <span className="text-gray-900">{formData.chiefMourner} ({formData.chiefMournerRelationship})</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">연락처</span>
                <span className="text-gray-900">{formData.chiefMournerPhone}</span>
              </div>

              {/* 추가 상주 */}
              {formData.additionalMourners && formData.additionalMourners.length > 0 && (
                <div className="flex">
                  <span className="w-24 font-semibold text-gray-700">추가상주</span>
                  <div className="space-y-1">
                    {formData.additionalMourners.map((mourner, index) => (
                      <div key={index} className="text-gray-900">
                        {mourner.name} ({mourner.relationship})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* 미리보기 액션 */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowPreview(false)}
            className="flex-1"
          >
            수정
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="flex-1"
          >
            등록
          </Button>
        </div>
      </div>
    );
  };

  // 미리보기 모드일 때
  if (showPreview) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              미리보기
            </h1>
            <p className="text-sm text-gray-600">
              부고 내용을 확인하고 등록해주세요.
            </p>
          </div>

          {renderPreview()}
        </div>
      </MainLayout>
    );
  }

  // 폼 입력 모드
  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary-900 mb-2">
            부고 작성
          </h1>
          <p className="text-sm text-gray-600">
            상주 정보를 입력해주세요.
          </p>
        </div>

        {/* Progress Bar */}
        <ProgressBar
          currentStep={3}
          totalSteps={3}
          steps={['고인 정보', '장례 정보', '상주 정보']}
          className="mb-8"
        />

        {/* Form */}
        <ObituaryForm
          formData={formData}
          onChange={handleChange}
          errors={errors}
          step="mourner"
        />

        {/* Actions */}
        <div className="flex gap-3 mt-8">
          <Button
            variant="outline"
            onClick={handlePrev}
            className="flex-1"
          >
            이전
          </Button>
          <Button
            variant="primary"
            onClick={handlePreview}
            className="flex-1"
          >
            미리보기
          </Button>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-accent-50 rounded-lg border border-accent-200">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">안내:</span> 미리보기에서 최종 확인 후 등록됩니다.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ObituaryCreateStep3;
